import { useEffect, useRef, useState } from 'react';

type UseRevealOptions = IntersectionObserverInit & {
  /**
   * When true, stays visible after the first intersection.
   */
  once?: boolean;
};

export function useReveal<T extends Element>(
  options: UseRevealOptions = { threshold: 0.2, once: true }
) {
  const { once = true, ...observerInit } = options;
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setIsVisible(false);
        }
      },
      {
        threshold: 0.2,
        ...observerInit,
      }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [observerInit.root, observerInit.rootMargin, observerInit.threshold, once]);

  return { ref, isVisible, setIsVisible };
}

export default useReveal;
